/**
 * GenerationLoadingScreen — AM2Loading per design canvas.
 *
 * Reuses the shared <OrbitalLoader /> (same rings + breathing sigil as
 * onboarding's Computing screen) — single loader implementation across the
 * miniapp.
 *
 * **Progress is 100% backend-driven.** The progress bar and chip counter
 * reflect ONLY the real number of frames the worker has delivered
 * (`framesReady`). There is no time-based shimmer or fake countdown — when
 * the worker hasn't reported anything yet, the bar shows an indeterminate
 * sliding-stripe animation (clearly not a percentage).
 *
 * Copy stays in i18n (avatarFlow.loader.*) and uses the mystical voice
 * (Хроники Акаши / врата времени) — never names the model/provider.
 */
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../brand/CosmosBackdrop';
import { OrbitalLoader } from '../brand/OrbitalLoader';
import styles from './GenerationLoadingScreen.module.css';

export type LoaderPhase = 'queued' | 'composing' | 'painting' | 'slicing' | 'finalizing';

export interface GenerationLoadingScreenProps {
  /** Frames the loader is brewing — drives "{{n}} frames rendered" copy. */
  totalFrames?: number;
  /** Real frames already delivered by the worker (drives true progress). */
  framesReady?: number;
  /** Pay chip price (Telegram Stars). Omit to hide chip. */
  paidStars?: number;
  /** Coarse-grained worker phase from backend. */
  phase?: LoaderPhase | null;
  testId?: string;
}

const PHASE_ORDER: LoaderPhase[] = ['queued', 'composing', 'painting', 'slicing', 'finalizing'];

function phaseReached(current: LoaderPhase | null | undefined, target: LoaderPhase): boolean {
  if (!current) return false;
  return PHASE_ORDER.indexOf(current) >= PHASE_ORDER.indexOf(target);
}

// Honest phase-based progress: each phase = real backend work step.
// painting is the longest (~70% of total time), so it gets a wider band.
const PHASE_PCT: Record<LoaderPhase, number> = {
  queued:     5,
  composing:  20,
  painting:   45,
  slicing:    75,
  finalizing: 90,
};

export function GenerationLoadingScreen({
  totalFrames = 9,
  framesReady,
  paidStars,
  phase,
  testId = 'generation-loading-screen',
}: GenerationLoadingScreenProps): JSX.Element {
  const { t } = useTranslation('flows');

  const framesDone = Math.max(0, Math.min(totalFrames, framesReady ?? 0));
  const hasRealProgress = framesDone > 0;
  const framesLeft = Math.max(0, totalFrames - framesDone);
  // Phase-driven chip activation (independent from framesReady — frames only
  // arrive at the very end as one atomic batch).
  const composingDone = phaseReached(phase, 'painting');
  const paintingActive = phase === 'painting';
  const paintingDone = phaseReached(phase, 'slicing');
  const slicingActive = phase === 'slicing' || phase === 'finalizing';
  // Progress bar: frames-done wins (100% at end), otherwise phase-based (real backend steps).
  const phasePct = phase ? PHASE_PCT[phase] : 0;
  const barPct = hasRealProgress ? 100 : phasePct;

  return (
    <div className={styles.root} data-testid={testId}>
      <CosmosBackdrop />

      <main className={styles.content}>
        <OrbitalLoader />

        {paidStars != null ? (
          <div className={styles.paidChip} role="status" aria-live="polite">
            <span aria-hidden="true">●</span>{' '}
            {t('avatarFlow.loader.paidChip', {
              price: paidStars,
              defaultValue: '{{price}} ⭐ paid · brewing',
            })}
          </div>
        ) : null}

        <h2 className={styles.title} aria-live="polite">
          {t('avatarFlow.loader.title.prefix', {
            defaultValue: 'Opening the gates of time',
          })}{' '}
          <em className={styles.titleAccent}>
            {t('avatarFlow.loader.title.suffix', {
              count: totalFrames,
              defaultValue: 'weaving your {{count}} portraits…',
            })}
          </em>
        </h2>

        <ul className={styles.progressChips} aria-live="polite">
          {/* Chip 1: composing scenes (Gemini) */}
          <li className={composingDone ? styles.chipDone : styles.chipActive}>
            <span aria-hidden="true">{composingDone ? '✓' : '↻'}</span>{' '}
            {t('avatarFlow.loader.chip.gates', {
              defaultValue: 'The Akashic chronicles unfurl',
            })}
          </li>
          {/* Chip 2: painting (gpt-image — the long pole) */}
          <li className={paintingDone ? styles.chipDone : paintingActive ? styles.chipActive : styles.chipPending}>
            <span aria-hidden="true">{paintingDone ? '✓' : paintingActive ? '↻' : '·'}</span>{' '}
            {hasRealProgress
              ? t('avatarFlow.loader.chip.framesDone', {
                  done: framesDone,
                  left: framesLeft,
                  defaultValue: '{{done}} portraits rendered',
                })
              : t('avatarFlow.loader.chip.painting', {
                  defaultValue: 'Painting in the cosmic atelier',
                })}
          </li>
          {/* Chip 3: slicing/finalizing */}
          <li className={hasRealProgress ? styles.chipDone : slicingActive ? styles.chipActive : styles.chipPending}>
            <span aria-hidden="true">{hasRealProgress ? '✓' : slicingActive ? '↻' : '·'}</span>{' '}
            {t('avatarFlow.loader.chip.polish', {
              defaultValue: 'Weaving threads of karma…',
            })}
          </li>
        </ul>

        {barPct > 0 ? (
          <div className={styles.progressTrack} aria-hidden="true">
            <div
              className={styles.progressFill}
              style={{ width: `${barPct}%` }}
            />
          </div>
        ) : (
          // No phase yet → indeterminate stripe. Never shows a fake percentage.
          <div
            className={`${styles.progressTrack} ${styles.progressIndeterminate}`}
            aria-hidden="true"
          />
        )}
        <div className={styles.timeHint}>
          {t('avatarFlow.loader.timeLeftIndeterminate', {
            defaultValue: 'You can leave — we will buzz you the moment it’s ready',
          })}
        </div>
      </main>
    </div>
  );
}

export default GenerationLoadingScreen;
